import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, getCountFromServer, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Link } from 'react-router-dom';
import { Users, FolderKanban, Send, ChevronRight, UserCheck, UserCog } from 'lucide-react';
import { toast } from 'react-toastify';

interface KPIs {
  totalUsers: number;
  requesters: number;
  providers: number;
  totalProjects: number;
  totalProposals: number;
}

interface RecentProject {
  id: string;
  title: string;
  requesterName: string;
  createdAt: Date;
  status: string;
}

interface RecentUser {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: Date;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  active: 'En cours',
  closed: 'Clôturé',
  cancelled: 'Annulé',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};

const ROLE_LABELS: Record<string, string> = {
  requester: 'Demandeur',
  provider: 'Prestataire',
  admin: 'Admin',
};

const formatDate = (date: Date) =>
  date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const AdminDashboard = () => {
  const [kpis, setKpis] = useState<KPIs>({
    totalUsers: 0,
    requesters: 0,
    providers: 0,
    totalProjects: 0,
    totalProposals: 0,
  });
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>([]);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const usersRef = collection(db, 'users');
        const projectsRef = collection(db, 'projects');
        const proposalsRef = collection(db, 'proposals');

        // KPI counts
        const [
          totalUsersCount,
          requestersCount,
          providersCount,
          totalProjectsCount,
          totalProposalsCount,
        ] = await Promise.all([
          getCountFromServer(usersRef),
          getCountFromServer(query(usersRef, where('role', '==', 'requester'))),
          getCountFromServer(query(usersRef, where('role', '==', 'provider'))),
          getCountFromServer(projectsRef),
          getCountFromServer(proposalsRef),
        ]);

        setKpis({
          totalUsers: totalUsersCount.data().count,
          requesters: requestersCount.data().count,
          providers: providersCount.data().count,
          totalProjects: totalProjectsCount.data().count,
          totalProposals: totalProposalsCount.data().count,
        });

        // Last 5 projects
        const projectsSnap = await getDocs(
          query(projectsRef, orderBy('createdAt', 'desc'), limit(5))
        );

        // Build user map for requester names
        const usersSnap = await getDocs(usersRef);
        const usersMap: Record<string, string> = {};
        usersSnap.docs.forEach(d => {
          const data = d.data();
          usersMap[d.id] = data.username || data.email || d.id;
        });

        const projects: RecentProject[] = projectsSnap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            title: data.name || data.title || '—',
            requesterName: usersMap[data.userId] || '—',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
            status: data.status || 'pending',
          };
        });
        setRecentProjects(projects);

        // Last 5 users (sorted by createdAt desc)
        const sortedUsers = usersSnap.docs
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              name: data.username || '—',
              email: data.email || '—',
              role: data.role || '—',
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
            };
          })
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 5);
        setRecentUsers(sortedUsers);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Erreur lors du chargement du tableau de bord');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Tableau de bord admin</h1>

        {/* ── SECTION 1 — KPIs ── */}
        <section className="mb-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Total users */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col items-center text-center">
              <Users className="h-6 w-6 text-primary-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{kpis.totalUsers}</p>
              <p className="text-sm text-gray-500 mt-1">Utilisateurs</p>
            </div>

            {/* Demandeurs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col items-center text-center">
              <UserCheck className="h-6 w-6 text-green-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{kpis.requesters}</p>
              <p className="text-sm text-gray-500 mt-1">Demandeurs</p>
            </div>

            {/* Prestataires */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col items-center text-center">
              <UserCog className="h-6 w-6 text-blue-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{kpis.providers}</p>
              <p className="text-sm text-gray-500 mt-1">Prestataires</p>
            </div>

            {/* Projects */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col items-center text-center">
              <FolderKanban className="h-6 w-6 text-orange-500 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{kpis.totalProjects}</p>
              <p className="text-sm text-gray-500 mt-1">Projets</p>
            </div>

            {/* Proposals */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col items-center text-center">
              <Send className="h-6 w-6 text-teal-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{kpis.totalProposals}</p>
              <p className="text-sm text-gray-500 mt-1">Propositions</p>
            </div>
          </div>
        </section>

        {/* ── SECTION 2 — Quick navigation ── */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Navigation rapide</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/admin/projects"
              className="group bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between hover:border-primary-400 hover:shadow-md transition-all"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-orange-50 rounded-lg p-3">
                  <FolderKanban className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Gestion des Projets</p>
                  <p className="text-sm text-gray-500">{kpis.totalProjects} projet{kpis.totalProjects !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
            </Link>

            <Link
              to="/admin/users"
              className="group bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between hover:border-primary-400 hover:shadow-md transition-all"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-green-50 rounded-lg p-3">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Gestion des Utilisateurs</p>
                  <p className="text-sm text-gray-500">{kpis.totalUsers} utilisateur{kpis.totalUsers !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
            </Link>

            <Link
              to="/admin/kpi"
              className="group bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between hover:border-primary-400 hover:shadow-md transition-all"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-teal-50 rounded-lg p-3">
                  <Send className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Gestion des Propositions</p>
                  <p className="text-sm text-gray-500">{kpis.totalProposals} proposition{kpis.totalProposals !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
            </Link>
          </div>
        </section>

        {/* ── SECTION 3 — Recent activity ── */}
        <section>
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Activité récente</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Last 5 projects */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center">
                  <FolderKanban className="h-4 w-4 text-orange-500 mr-2" />
                  Derniers projets
                </h3>
                <Link to="/admin/projects" className="text-xs text-primary-600 hover:underline">Voir tout</Link>
              </div>
              {recentProjects.length === 0 ? (
                <p className="px-6 py-6 text-sm text-gray-400 italic">Aucun projet.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {recentProjects.map(p => (
                    <li key={p.id} className="px-6 py-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{p.requesterName} · {formatDate(p.createdAt)}</p>
                      </div>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-700'}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Last 5 users */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center">
                  <Users className="h-4 w-4 text-green-600 mr-2" />
                  Derniers inscrits
                </h3>
                <Link to="/admin/users" className="text-xs text-primary-600 hover:underline">Voir tout</Link>
              </div>
              {recentUsers.length === 0 ? (
                <p className="px-6 py-6 text-sm text-gray-400 italic">Aucun utilisateur.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {recentUsers.map(u => (
                    <li key={u.id} className="px-6 py-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{u.email} · {formatDate(u.createdAt)}</p>
                      </div>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.role === 'requester' ? 'bg-green-100 text-green-800'
                        : u.role === 'provider' ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-700'
                      }`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
